import React, { Fragment } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button, ButtonToolbar, Page } from '../uiold';
import LoadingIndicator from '../uiold/LoadingIndicator';
import { showError, showSuccess } from '../../components/App/AppAlert';

import FormBuilder from './FormBuilder';

import { EvaluationFormDefinition as FormDef } from '../../types/shared/EvaluationForm';
import { useFetchJson } from '../../support/useFetch';
import { useImmer } from './useImmer';

function BuilderPage(props: {
  formDef: FormDef;
  onSaved: (formDef: FormDef) => void;
}) {
  let fetchJson = useFetchJson();
  let [formDef, updateFormDef, setFormDef] = useImmer<FormDef>(props.formDef);
  let revert = () => {
    setFormDef(props.formDef);
  };
  let isDirty = formDef !== props.formDef;

  let { mutate: save, isLoading: isSaving } = useMutation(
    ['forms/evaluation'],
    async (formDef: FormDef) => {
      await fetchJson('/api/forms/evaluation', { body: formDef });
      showSuccess(t('Saved Successfully.'));
      props.onSaved(formDef);
    },
    {
      onError: (error) => {
        showError(String(error));
      },
    },
  );

  if (formDef == null) {
    return <Page isLoading={true} />;
  }

  return (
    <Page>
      <Page.Header title={t('Report Builder')}>
        {isDirty && (
          <ButtonToolbar>
            {isSaving ? (
              <LoadingIndicator />
            ) : (
              <Fragment>
                <Button title={t('Revert')} onPress={() => revert()} />
                <Button
                  isPrimary
                  title={t('Save Changes')}
                  onPress={() => {
                    formDef && save(formDef);
                  }}
                />
              </Fragment>
            )}
          </ButtonToolbar>
        )}
      </Page.Header>
      <FormBuilder formDef={formDef} update={updateFormDef} />
    </Page>
  );
}

export default function BuilderPageOuter() {
  const fetchJson = useFetchJson();

  const { data: formDef, isLoading, error, refetch } = useQuery(
    ['forms/evaluation'],
    () => fetchJson<FormDef>('/api/forms/evaluation'),
  );

  if (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return <p>{t('Error: {errorMessage}', { errorMessage })}</p>;
  }

  if (isLoading || !formDef) {
    return <p>{t('Loading...')}</p>;
  }

  return <BuilderPage formDef={formDef} onSaved={() => refetch()} />;
}
